import { gql } from "@apollo/client";
import { Classes } from "@blueprintjs/core";
import { toast } from "sonner";

import { Box, Flex } from "@/components/layout/flexbox";
import { ToggleField } from "@/components/pieces/form/ToggleField";
import { SUBTEXT_COLOR } from "@/css/constants";
import {
  ProductDetailQuery,
  useToggleAcceptingNewClientsMutation,
} from "@/graphql";

type Product = ProductDetailQuery["productById"]["coachProduct"];

export const TOGGLE_ACCEPTING_NEW_CLIENTS = gql`
  mutation ToggleAcceptingNewClients($coachProductId: ID!) {
    toggleAcceptingNewClients(coachProductId: $coachProductId) {
      ok
    }
  }
`;

interface AcceptingNewClientsProps {
  coachProduct: Product;
}

export function AcceptingNewClients({
  coachProduct,
}: AcceptingNewClientsProps) {
  const [toggleAcceptingNewClients, { loading }] =
    useToggleAcceptingNewClientsMutation();

  const handleToggleAcceptingNewClients = async () => {
    try {
      await toggleAcceptingNewClients({
        variables: {
          coachProductId: coachProduct.id,
        },
        optimisticResponse: {
          toggleAcceptingNewClients: {
            ok: true,
          },
        },
        update: (cache) => {
          cache.modify({
            id: cache.identify(coachProduct),
            fields: {
              acceptingNewClients: () => !coachProduct.acceptingNewClients,
            },
          });
        },
      });

      toast.success("Saved");
    } catch {
      toast.error("An error occurred. Please try again.");
    }
  };

  return (
    <Flex justifyContent="space-between" alignItems="center" pt={4}>
      <Box flexGrow={1}>
        <Box fontSize={20} fontWeight={600}>
          Accepting New Clients
        </Box>
        <Box
          fontSize={16}
          className={loading ? Classes.SKELETON : ""}
          color={SUBTEXT_COLOR}
        >
          Allow new clients to book a session
        </Box>
      </Box>
      <Flex justifyContent="right" flexBasis={60} flexShrink={0}>
        {loading ? (
          <Box className={Classes.SKELETON} height={26} width={46} />
        ) : (
          <ToggleField
            checked={coachProduct?.acceptingNewClients ?? false}
            onChange={handleToggleAcceptingNewClients}
          />
        )}
      </Flex>
    </Flex>
  );
}
